<ng-template #analysisHeaderControls>
  @if (analysis(); as headerAnalysis) {
    <app-analysis-status-selector
      [analysis]="headerAnalysis"
      [disabled]="!currentOrgStore.can('analysis', 'update')"
      (statusChanged)="onStatusChanged($event)"
    />
  }
</ng-template>

<div class="ks-page-wide">
  @if (currentOrgStore.can('analysis', 'read') && !currentOrgStore.can('analysis', 'update')) {
    <p role="status" class="mb-4 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
      Lesezugriff: Sie können Ergebnisse, Bilder und Berichte ansehen. Änderungen und neue Berechnungen sind nicht freigegeben.
    </p>
  }
  @if (analysis(); as analysisData) {
    <hlm-tabs [tab]="activeTab()" class="block w-full">
      <section hlmTabsContent="overview" class="!mt-0">
        <section class="mb-6 border-y border-border bg-background">
          <div class="flex min-h-14 flex-wrap items-center gap-3 py-3">
            <button
              type="button"
              class="flex min-w-0 flex-1 basis-64 flex-wrap items-center gap-3 text-left"
              [attr.aria-expanded]="executionExpanded()"
              aria-controls="analysis-execution-details"
              (click)="toggleExecutionExpanded()"
            >
              <span
                class="size-2 shrink-0 rounded-full"
                [class]="executionSummary().dotClass"
              ></span>
              <span class="text-base font-bold text-foreground">
                {{ executionSummary().title }}
              </span>
              <span class="truncate text-dense text-muted-foreground">
                {{ executionSummary().subtitle }}
              </span>
            </button>

            @if (currentOrgStore.can('analysis', 'startAnalysis')) {
            <button
              type="button"
              role="switch"
              class="inline-flex shrink-0 items-center gap-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
              [attr.aria-checked]="progressCardTab() === 'details'"
              (click)="selectProgressCardTab(progressCardTab() === 'details' ? 'progress' : 'details')"
            >
              <span
                class="relative h-[13px] w-6 rounded-full border border-border transition-colors"
                [class.bg-primary]="progressCardTab() === 'details'"
                [class.bg-muted]="progressCardTab() !== 'details'"
              >
                <span
                  class="absolute left-0.5 top-1/2 size-[9px] -translate-y-1/2 rounded-full bg-background shadow-sm transition-transform"
                  [class.translate-x-[11px]]="progressCardTab() === 'details'"
                ></span>
              </span>
              Technisch
            </button>
            }

            <button
              type="button"
              class="inline-flex shrink-0 items-center gap-1.5 font-mono text-2xs text-muted-foreground hover:text-foreground"
              [attr.aria-expanded]="executionExpanded()"
              aria-controls="analysis-execution-details"
              (click)="toggleExecutionExpanded()"
            >
              {{ executionExpanded() ? "Details ausblenden" : "Details anzeigen" }}
              <ng-icon
                name="lucideChevronDown"
                class="size-3.5 transition-transform"
                [class.rotate-180]="executionExpanded()"
              />
            </button>
          </div>

          @if (executionExpanded()) {
            <div id="analysis-execution-details" class="pb-4">
              @if (progressCardTab() === "progress") {
                <app-analysis-timeline
                  timelineMode="technical"
                  [technicalStatus]="analysisData.technicalStatus ?? null"
                  [runtime]="runtimeData()"
                  [executionWindow]="workflowFacade.activeTaskWindow()"
                  [showActions]="currentOrgStore.can('analysis', 'startAnalysis')"
                  (actionExecuted)="onTimelineActionExecuted($event)"
                />
              } @else {
                <app-analysis-detail-debug-panel
                  [analysisId]="analysisData.analysisId"
                  [createdAt]="analysisData.createdAt"
                  [runtime]="runtimeData()"
                  [technicalStatus]="analysisData.technicalStatus ?? null"
                  [isResettingState]="workflowFacade.isResettingState()"
                  (actionExecuted)="onTimelineActionExecuted($event)"
                  (resetStateRequested)="resetStuckState()"
                />
              }
            </div>
          }
        </section>

        <section class="pt-1">
          <div class="flex items-baseline gap-3 pb-3">
            <h2 class="text-base font-bold text-foreground">Visualisierung</h2>
            @if (coordinatesLabel()) {
              <span class="ml-auto font-mono text-2xs text-muted-foreground">
                {{ coordinatesLabel() }}
              </span>
            }
          </div>
          <div class="h-[440px] overflow-hidden rounded-2xl bg-muted">
            <app-analysis-viewer
              [analysis]="analysisData"
              [planningId]="selectedPlanningId()"
              [filesOverview]="filesOverview() || []"
              [visibleViews]="['map', '2d', '3d']"
              [externalPolygons]="polygons()"
              [expandable]="true"
              [canEditMarker]="currentOrgStore.can('analysis', 'update')"
              (markerMoved)="onMapCoordinatesChanged($event)"
              (expandRequested)="openViewerDialog($event)"
              (mediaRefreshRequested)="reloadFilesOverview()"
            />
          </div>
        </section>

        @if (currentOrgStore.can('analysis', 'update')) {
        <hlm-dialog
          [state]="addressDialogOpen() ? 'open' : 'closed'"
          (closed)="addressDialogOpen.set(false)"
        >
          <hlm-dialog-content
            *brnDialogContent="let ctx"
            class="!w-[620px] !max-w-[calc(100vw-48px)] !gap-0 !overflow-hidden !rounded-[18px] !p-0"
          >
            <hlm-dialog-header class="px-[26px] pb-5 pt-[22px] text-left">
              <p class="font-mono text-2xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
                Analyse {{ analysisData.analysisId.slice(-8) }}
              </p>
              <h2 hlmDialogTitle class="mt-1 text-xl font-bold tracking-tight">Eingabedaten bearbeiten</h2>
            </hlm-dialog-header>

            <app-analysis-detail-address-details class="block border-t border-border"
              [analysis]="analysisData"
              [saveStatus]="updateFacade.detailsSaveStatus()"
              [lastSavedAt]="updateFacade.detailsLastSavedAt()"
              (detailsUpdated)="onDetailsUpdated($event)"
              (closeRequested)="addressDialogOpen.set(false)"
            />
          </hlm-dialog-content>
        </hlm-dialog>
        }
      </section>

      <section hlmTabsContent="roof" class="!mt-0">
        <app-analysis-viewer
          [analysis]="analysisData"
          [filesOverview]="filesOverview() || []"
          [externalPolygons]="polygons()"
          [showPolygons]="true"
          [enablePolygonEditing]="currentOrgStore.can('analysis', 'update')"
          [canEditMarker]="currentOrgStore.can('analysis', 'update')"
          [solarStatusSaving]="updateFacade.isSavingHasSolar()"
          (detectionRequested)="startRoofDetection()"
          (polygonsSynced)="reloadPolygons()"
          (deviationPreferenceChanged)="onDeviationPreferenceChanged($event)"
          (solarStatusChange)="onHasSolarChanged($event)"
        />
      </section>

      <section hlmTabsContent="planning" class="mt-6">
        @if (planningTaskFailed() && plannings().length === 0) {
          <div class="rounded-2xl border border-dashed border-destructive/30">
            <app-state-panel
              state="error"
              [large]="true"
              title="Planung fehlgeschlagen"
              [description]="planningFailureDescription()"
            >
              @if (currentOrgStore.can('analysis', 'startPlanning')) {
              <button
                type="button"
                class="inline-flex h-8 items-center gap-2 rounded-lg bg-primary px-3 text-xs font-normal text-primary-foreground transition-colors hover:bg-primary/90"
                (click)="onCreatePlanning()"
              >
                Planung erneut starten
              </button>
              }
            </app-state-panel>
          </div>
        } @else if (planningTabDisabled() && !isLoadingPlannings() && plannings().length === 0) {
          <div class="rounded-2xl border border-dashed border-border">
            <app-state-panel
              state="empty"
              [large]="true"
              title="Noch keine Planung berechnet"
              description="Wählen Sie in der Ausführungsleiste die Planungsstufe, um Aufgaben 4 und 5 zu starten."
            />
          </div>
        } @else {
          <app-analysis-planning-results-view
            [analysis]="analysisData"
            [polygons]="polygons()"
            [filesOverview]="filesOverview() || []"
            [plannings]="plannings() || []"
            [selectedPlanningId]="selectedPlanningId()"
            [primaryPlanningId]="primaryPlanningId()"
            [isLoadingPlannings]="isLoadingPlannings()"
            [isDeletingPlanning]="deletingPlanning()"
            [canCreatePlanning]="currentOrgStore.can('analysis', 'startPlanning')"
            [mode]="currentOrgStore.can('analysis', 'startPlanning') ? 'admin' : 'public'"
            [isVisible]="activeTab() === 'planning'"
            (selectedPlanningIdChange)="onSelectedPlanningIdChange($event)"
            (deletePlanning)="onDeletePlanning($event)"
            (setPrimaryPlanning)="onSetPrimaryPlanning($event)"
            (createPlanning)="onCreatePlanning()"
            (retryPlanning)="onRetryPlanning($event)"
            (mediaRefreshRequested)="reloadFilesOverview()"
          />
        }
      </section>

      <section hlmTabsContent="files" class="mt-6">
        <app-analysis-files-tab
          [filesOverview]="filesOverview() || []"
          [selectedMediaId]="analysisData.selectedMediaId || null"
          [isLoading]="isLoadingFiles()"
        />
      </section>

      <section hlmTabsContent="customer" class="mt-6">
        <app-analysis-customer-tab
          [analysis]="analysisData"
          [filesOverview]="filesOverview() || []"
          [canEdit]="currentOrgStore.can('analysis', 'update')"
          (filesRequested)="selectTab('files')"
        ></app-analysis-customer-tab>
      </section>
    </hlm-tabs>

    <hlm-dialog [state]="viewerDialogState()" (closed)="closeViewerDialog()">
      <hlm-dialog-content
        *brnDialogContent="let ctx"
        class="h-[85vh] !w-[92vw] !max-w-[1400px] overflow-hidden p-0 [&>button]:z-[1100] [&>button]:rounded-lg [&>button]:bg-foreground/65 [&>button]:text-white"
      >
        <h2 hlmDialogTitle class="sr-only">Visualisierung</h2>
        <p hlmDialogDescription class="sr-only">Vergrößerte Darstellung der Analyse</p>
        <app-analysis-viewer
      [initialView]="expandedViewerSelection()"
          [analysis]="analysisData"
          [planningId]="selectedPlanningId()"
          [filesOverview]="filesOverview() || []"
          [visibleViews]="['map', '2d', '3d']"
          [externalPolygons]="polygons()"
          [canEditMarker]="currentOrgStore.can('analysis', 'update')"
          (markerMoved)="onMapCoordinatesChanged($event)"
          (mediaRefreshRequested)="reloadFilesOverview()"
        />
      </hlm-dialog-content>
    </hlm-dialog>
  } @else if (isLoading()) {
    <app-skeleton-loader variant="full" />
  } @else if (error()) {
    <div class="rounded-2xl border border-dashed border-destructive/30">
      <app-state-panel
        state="error"
        [large]="true"
        title="Analyse konnte nicht geladen werden"
        [description]="error()"
      >
        <button
          type="button"
          class="inline-flex h-8 items-center gap-2 rounded-lg bg-primary px-3 text-xs font-normal text-primary-foreground transition-colors hover:bg-primary/90"
          (click)="loadAnalysis()"
        >
          Erneut versuchen
        </button>
      </app-state-panel>
    </div>
  }
</div>
